import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { tokenStorage } from '@/lib/auth/tokenStorage'
import { authRepository } from '../services/authRepository'
import type { LoginCredentials } from '../types/auth'

export const AUTH_KEYS = {
  user: ['auth', 'user'] as const,
}

// ─── Current User ─────────────────────────────────────────────────────────────

export function useCurrentUser() {
  return useQuery({
    queryKey: AUTH_KEYS.user,
    queryFn: () => authRepository.me(),
    staleTime: 1000 * 60 * 5,
    retry: false,
    enabled: tokenStorage.hasTokens(),
  })
}

// ─── Login ────────────────────────────────────────────────────────────────────

export function useLogin() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => authRepository.login(credentials),
    onSuccess: (result) => {
      qc.setQueryData(AUTH_KEYS.user, result.user)
      void navigate('/dashboard', { replace: true })
    },
  })
}

// ─── Logout ───────────────────────────────────────────────────────────────────

export function useLogout() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: () => authRepository.logout(),
    onSuccess: () => {
      qc.clear()
      void navigate('/login', { replace: true })
    },
  })
}

// ─── Refresh ──────────────────────────────────────────────────────────────────

export function useRefreshToken() {
  return useMutation({
    mutationFn: () => authRepository.refresh(),
  })
}
